import { useEffect, useId, useRef, useState } from "react";
import styles from "./Autocomplete.module.scss";

/**
 * Shared Autocomplete — a debounced typeahead that reports BOTH the visible text
 * and the selected record.
 *
 * Source-agnostic: it takes a `fetcher` and two accessors, so it works against any
 * endpoint. The product-catalogue wiring lives at the call site, not here.
 *
 * ```jsx
 * <Autocomplete
 *   value={values.product_name}
 *   fetcher={globalSearch}
 *   getOptionLabel={(p) => p.ChemicalName}
 *   getOptionValue={(p) => p.id}
 *   onChange={({ label, value }) => …}      // typing → value === null
 *   inputProps={{ placeholder: "Product Name*", className: styles.input }}
 * />
 * ```
 *
 * ── The two things that make this fiddly, both handled here ──────────────────
 *
 * 1. **Selection must be distinguishable from typing.** `onChange` always reports
 *    `{ label, value }`; `value` is the selected record's id, or **null** when the
 *    user has typed since selecting. Callers rely on that to avoid submitting an
 *    id that contradicts the visible text — worse than sending no id, because the
 *    backend would trust it.
 *
 * 2. **blur fires before click.** Closing the list on blur would unmount the
 *    option before its handler ran, so selections would silently do nothing.
 *    Options fire on `onMouseDown` (before blur) and the blur close is deferred.
 */
const DEFAULT_MIN_CHARS = 2;
const DEFAULT_DEBOUNCE_MS = 300;

export default function Autocomplete({
  value = "",
  onChange,
  fetcher,
  getOptionLabel = (o) => o?.label ?? String(o ?? ""),
  getOptionValue = (o) => o?.id,
  getOptionKey,
  minChars = DEFAULT_MIN_CHARS,
  debounceMs = DEFAULT_DEBOUNCE_MS,
  noResultsText = "No results found",
  inputProps = {},
  trailing,
  className = "",
}) {
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [searching, setSearching] = useState(false);
  const debounceRef = useRef(null);
  const blurRef = useRef(null);
  // Stable, SSR-safe id so the input's `aria-controls` can point at the listbox —
  // required for role="combobox" to be announced correctly.
  const listboxId = `${useId()}-listbox`;
  // Guards against a slow response landing after a newer keystroke and
  // overwriting fresher results.
  const queryRef = useRef("");

  // Clear both timers on unmount, so a pending debounce cannot setState on an
  // unmounted component.
  useEffect(
    () => () => {
      clearTimeout(debounceRef.current);
      clearTimeout(blurRef.current);
    },
    [],
  );

  const runSearch = (query) => {
    clearTimeout(debounceRef.current);
    queryRef.current = query;

    if (!query || query.length < minChars) {
      setResults([]);
      setOpen(false);
      setSearching(false);
      return;
    }

    setSearching(true);
    debounceRef.current = setTimeout(async () => {
      let data;
      try {
        data = await fetcher(query);
      } catch {
        data = null;
      }
      // Drop a stale response: the input has moved on since this fired.
      if (queryRef.current !== query) return;
      // Several APIs here resolve to `false` rather than throwing, so this guard
      // is doing real work, not just satisfying types.
      setResults(Array.isArray(data) ? data : []);
      setOpen(true);
      setSearching(false);
    }, debounceMs);
  };

  const handleInput = (e) => {
    const label = e.target.value;
    // Typing invalidates any previous selection — see note 1 above.
    onChange?.({ label, value: null, option: null });
    runSearch(label);
  };

  const handleSelect = (option) => {
    onChange?.({
      label: getOptionLabel(option),
      value: getOptionValue(option),
      option,
    });
    setOpen(false);
    setResults([]);
  };

  return (
    <span className={`${styles.wrap} ${className}`}>
      <input
        {...inputProps}
        type="text"
        value={value}
        onChange={handleInput}
        onFocus={() => {
          if (value.length >= minChars && results.length > 0) setOpen(true);
        }}
        onBlur={() => {
          blurRef.current = setTimeout(() => setOpen(false), 150);
        }}
        onKeyDown={(e) => {
          if (e.key === "Escape") setOpen(false);
        }}
        autoComplete="off"
        role="combobox"
        aria-expanded={open}
        aria-controls={listboxId}
        aria-autocomplete="list"
      />

      {/* The consumer's trailing icon is hidden while searching so the spinner
          does not sit on top of it. */}
      {searching ? (
        <span className={styles.spinner} aria-hidden="true" />
      ) : (
        trailing
      )}

      {open && (
        <ul id={listboxId} className={styles.dropdown} role="listbox">
          {results.length > 0 ? (
            results.map((option, i) => (
              <li
                key={getOptionKey?.(option) ?? getOptionValue(option) ?? i}
                role="option"
                aria-selected="false"
              >
                <button
                  type="button"
                  className={styles.option}
                  // onMouseDown, not onClick — see note 2 above.
                  onMouseDown={() => handleSelect(option)}
                >
                  {getOptionLabel(option)}
                </button>
              </li>
            ))
          ) : (
            <li className={styles.noResults}>{noResultsText}</li>
          )}
        </ul>
      )}
    </span>
  );
}
